import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  NgZone,
} from '@angular/core';
import { PrettyNumberPipe } from '../pipes/pretty-number.pipe';

type Easing = 'easeOutCubic' | 'easeOutQuad';

@Directive({
  selector: '[appCountUp]',
  standalone: true,
  providers: [PrettyNumberPipe],
})
export class CountUpDirective implements OnChanges, OnDestroy {
  /** Target value to count to */
  @Input('appCountUp') end = 0;

  /** How long the animation lasts (ms) */
  @Input() duration = 700;

  /** Easing curve */
  @Input() easing: Easing = 'easeOutCubic';

  /** Optional unit to pass to PrettyNumberPipe */
  @Input() unit?: string;

  /** Whether to compact/abbreviate (keeps parity with your pipe’s first arg) */
  @Input() compact: boolean = true;

  @Input() percentDigits: number = 2; // how many decimals to SHOW for %
  @Input() integerSteps = true; // optional: snap to whole numbers

  /** Start value; updated after each run so we animate from the previous number */
  private start: number | null = null;
  private rafId: number | null = null;
  private readonly epsilon = 1e-6;
  private readonly rawValueAttr = 'data-count-up-raw';

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone,
    private pretty: PrettyNumberPipe
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('end' in changes) {
      this.animate();
    }
  }

  ngOnDestroy(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private ease(t: number): number {
    switch (this.easing) {
      case 'easeOutQuad':
        return 1 - (1 - t) * (1 - t);
      case 'easeOutCubic':
      default:
        return 1 - Math.pow(1 - t, 3);
    }
  }

  private animate() {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const to = this._coerceToNumber(this.end);

    if (to == null) {
      this.start = null;
      this._renderValue(null);
      return;
    }

    const stored = this._readStoredValue();
    const from = stored ?? this.start ?? to;
    this.start = from;
    const dur = Math.max(0, this.duration);

    if (dur === 0 || Math.abs(to - from) < this.epsilon) {
      this.start = to;
      this._renderValue(to);
      return;
    }

    const t0 = performance.now();

    this.zone.runOutsideAngular(() => {
      const tick = (now: number) => {
        const t = dur === 0 ? 1 : Math.min(1, (now - t0) / dur);
        const eased = this.ease(t);
        let val = from + (to - from) * eased;

        // OPTIONAL: snap animation value BEFORE it hits the pipe
        if (this.unit === '%') {
          const pow = Math.pow(10, this.percentDigits);
          val = Math.round(val * pow) / pow; // e.g., 2 decimals
        } else if (this.integerSteps) {
          val = Math.round(val);
        }

        this._renderValue(val);
        this.start = val;

        if (t < 1) {
          this.rafId = requestAnimationFrame(tick);
        } else {
          this.start = to;
          this._storeValue(to);
          this.rafId = null;
        }
      };
      this.rafId = requestAnimationFrame(tick);
    });
  }

  private _renderValue(value: number | null) {
    const formatted =
      value == null
        ? ''
        : this.pretty.transform(value, this.compact, this.unit);
    this.el.nativeElement.textContent = String(formatted ?? '');
    if (value == null) {
      this._clearStoredValue();
    } else {
      this._storeValue(value);
    }
  }

  private _coerceToNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (value == null) {
      return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private _readStoredValue(): number | null {
    const attr = this.el.nativeElement.getAttribute(this.rawValueAttr);
    if (attr == null || attr === '') {
      return null;
    }
    return this._coerceToNumber(attr);
  }

  private _storeValue(value: number) {
    this.el.nativeElement.setAttribute(this.rawValueAttr, String(value));
  }

  private _clearStoredValue() {
    this.el.nativeElement.removeAttribute(this.rawValueAttr);
  }
}
